// theme.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from '../../config/config.service';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private readonly themeModeClasses = ['light', 'dark'];
    private readonly themeProfileClassPrefix = 'theme-profile-';
    private themeProfile: 'legacy' | 'updated' = 'updated';

    private themeSubject = new BehaviorSubject<string>(this.getTheme());
    theme$ = this.themeSubject.asObservable();

    // Colour roles
    primary: string;
    primaryVariant: string;
    secondary: string;
    secondaryVariant: string;
    background: string;
    surface: string;
    error: string;
    onPrimary: string;
    onSecondary: string;
    onBackground: string;
    onSurface: string;
    onError: string;
    primaryContainer: string;
    secondaryContainer: string;
    tertiary: string;
    onTertiary: string;
    tertiaryContainer: string;
    onTertiaryContainer: string;
    errorContainer: string;
    onErrorContainer: string;
    outline: string;
    outlineVariant: string;
    shadow: string;
    scrim: string;
    inverseSurface: string;
    inverseOnSurface: string;
    inversePrimary: string;
    surfaceTint: string;
    surfaceVariant: string;
    onSurfaceVariant: string;
    inverseSurfaceVariant: string;
    inverseOnSurfaceVariant: string;
    inversePrimaryVariant: string;
    inverseSecondary: string;
    inverseSecondaryVariant: string;
    inverseBackground: string;
    inverseError: string;
    inverseOnError: string;
    inversePrimaryContainer: string;
    inverseSecondaryContainer: string;
    inverseTertiary: string;
    inverseOnTertiary: string;
    inverseTertiaryContainer: string;
    inverseOnTertiaryContainer: string;

    constructor(private config: ConfigService) {}

    applyConfiguredTheme(): void {
        const configuredProfile = this.config.appMeta().themeProfile as unknown;
        this.themeProfile = this.resolveThemeProfile(configuredProfile);
        this.applyThemeClass(this.getTheme());
        this.getThemeColours();
    }

    private resolveThemeProfile(profile: unknown): 'legacy' | 'updated' {
        if (profile === 'legacy' || profile === 'updated') {
            return profile;
        }

        if (profile !== undefined && profile !== null) {
            console.warn(`Invalid appMeta.themeProfile '${String(profile)}'; falling back to 'updated'.`);
        }

        return 'updated';
    }

    setTheme(theme: string) {
        const normalizedTheme = this.normalizeTheme(theme);
        this.themeSubject.next(normalizedTheme);
        localStorage.setItem('theme', normalizedTheme);
        this.applyThemeClass(normalizedTheme);
        this.getThemeColours();
    }

    getTheme() {
        return this.normalizeTheme(localStorage.getItem('theme') || 'light');
    }

    private normalizeTheme(theme: string): 'light' | 'dark' {
        return theme === 'dark' ? 'dark' : 'light';
    }

    private applyThemeClass(theme: 'light' | 'dark'): void {
        const html = document.documentElement;
        html.classList.remove(...this.themeModeClasses);

        if (html.className.indexOf(this.themeProfileClassPrefix) !== -1) {
            const existing = html.className.split(/\s+/).filter((name) => name && !name.startsWith(this.themeProfileClassPrefix));
            html.className = existing.join(' ');
        }

        html.classList.add(theme, `${this.themeProfileClassPrefix}${this.themeProfile}`);
    }


    // Get specific colours, based on the theme
    private getThemeColours(): void {
        const root = document.querySelector('html');

        this.primary = getComputedStyle(root).getPropertyValue('--sys-primary');
        this.primaryVariant = getComputedStyle(root).getPropertyValue('--sys-primary-variant');
        this.secondary = getComputedStyle(root).getPropertyValue('--sys-secondary');
        this.secondaryVariant = getComputedStyle(root).getPropertyValue('--sys-secondary-variant');
        this.background = getComputedStyle(root).getPropertyValue('--sys-background');
        this.surface = getComputedStyle(root).getPropertyValue('--sys-surface');
        this.error = getComputedStyle(root).getPropertyValue('--sys-error');
        this.onPrimary = getComputedStyle(root).getPropertyValue('--sys-on-primary');
        this.onSecondary = getComputedStyle(root).getPropertyValue('--sys-on-secondary');
        this.onBackground = getComputedStyle(root).getPropertyValue('--sys-on-background');
        this.onSurface = getComputedStyle(root).getPropertyValue('--sys-on-surface');
        this.onError = getComputedStyle(root).getPropertyValue('--sys-on-error');
        this.primaryContainer = getComputedStyle(root).getPropertyValue('--sys-primary-container');
        this.secondaryContainer = getComputedStyle(root).getPropertyValue('--sys-secondary-container');
        this.tertiary = getComputedStyle(root).getPropertyValue('--sys-tertiary');
        this.onTertiary = getComputedStyle(root).getPropertyValue('--sys-on-tertiary');
        this.tertiaryContainer = getComputedStyle(root).getPropertyValue('--sys-tertiary-container');
        this.onTertiaryContainer = getComputedStyle(root).getPropertyValue('--sys-on-tertiary-container');
        this.errorContainer = getComputedStyle(root).getPropertyValue('--sys-error-container');
        this.onErrorContainer = getComputedStyle(root).getPropertyValue('--sys-on-error-container');
        this.outline = getComputedStyle(root).getPropertyValue('--sys-outline');
        this.outlineVariant = getComputedStyle(root).getPropertyValue('--sys-outline-variant');
        this.shadow = getComputedStyle(root).getPropertyValue('--sys-shadow');
        this.scrim = getComputedStyle(root).getPropertyValue('--sys-scrim');
        this.inverseSurface = getComputedStyle(root).getPropertyValue('--sys-inverse-surface');
        this.inverseOnSurface = getComputedStyle(root).getPropertyValue('--sys-inverse-on-surface');
        this.inversePrimary = getComputedStyle(root).getPropertyValue('--sys-inverse-primary');
        this.surfaceTint = getComputedStyle(root).getPropertyValue('--sys-surface-tint');
        this.surfaceVariant = getComputedStyle(root).getPropertyValue('--sys-surface-variant');
        this.onSurfaceVariant = getComputedStyle(root).getPropertyValue('--sys-on-surface-variant');
        this.inverseSurfaceVariant = getComputedStyle(root).getPropertyValue('--sys-inverse-surface-variant');
        this.inverseOnSurfaceVariant = getComputedStyle(root).getPropertyValue('--sys-inverse-on-surface-variant');
        this.inversePrimaryVariant = getComputedStyle(root).getPropertyValue('--sys-inverse-primary-variant');
        this.inverseSecondary = getComputedStyle(root).getPropertyValue('--sys-inverse-secondary');
        this.inverseSecondaryVariant = getComputedStyle(root).getPropertyValue('--sys-inverse-secondary-variant');
        this.inverseBackground = getComputedStyle(root).getPropertyValue('--sys-inverse-background');
        this.inverseError = getComputedStyle(root).getPropertyValue('--sys-inverse-error');
        this.inverseOnError = getComputedStyle(root).getPropertyValue('--sys-inverse-on-error');
        this.inversePrimaryContainer = getComputedStyle(root).getPropertyValue('--sys-inverse-primary-container');
        this.inverseSecondaryContainer = getComputedStyle(root).getPropertyValue('--sys-inverse-secondary-container');
        this.inverseTertiary = getComputedStyle(root).getPropertyValue('--sys-inverse-tertiary');
        this.inverseOnTertiary = getComputedStyle(root).getPropertyValue('--sys-inverse-on-tertiary');
        this.inverseTertiaryContainer = getComputedStyle(root).getPropertyValue('--sys-inverse-tertiary-container');
        this.inverseOnTertiaryContainer = getComputedStyle(root).getPropertyValue('--sys-inverse-on-tertiary-container');
    }

    onSurfaceColour(): string{
        return this.onSurface;
    }

}